import type { DetailedSavedSearch, SaleTypeType } from "@repo/database";
import type { SearchHookLocation } from "@repo/maps";
import {
  PUBLICLY_FILTERABLE_LISTING_STATUSES,
  type PubliclyFilterableListingStatus,
} from "./schema-utils";

const VALID_SALE_TYPES: SaleTypeType[] = [
  "ASSET_SALE",
  "STOCK_SALE",
  "HYBRID",
];

export type PriceDirectionFilter = "INCREASED" | "DECREASED" | null;

export type BusinessFilters = {
  location: SearchHookLocation | null;
  businessTypeIds: number[];
  statuses: PubliclyFilterableListingStatus[];
  listedBy: ListedByFilter[];
  saleTypes: SaleTypeType[];
  priceRange: RangeFilter<bigint>;
  revenueRange: RangeFilter<bigint>;
  profitRange: RangeFilter<bigint>;
  sdeRange: RangeFilter<bigint>;
  sdeMultipleRange: RangeFilter<number>;
  revenueMultipleRange: RangeFilter<number>;
  downPaymentRange: RangeFilter<bigint>;
  establishmentAgeRange: RangeFilter<number>;
  employeeCountRange: RangeFilter<number>;
  ownerWorkedHoursRange: RangeFilter<number>;
  propertyIncluded: boolean | null;
  financingAvailable: boolean | null;
  sbaPrequalified: boolean | null;
  visaQualified: boolean | null;
  includeMissingMultiples: boolean;
  priceDirection: PriceDirectionFilter;
  priceChangedWithinDays: number | null;
  minPriceChangePercent: number | null;
};

export type RangeFilter<T extends number | bigint> = {
  min: T | null;
  max: T | null;
};

export type ListedByFilter = "owner" | "broker";

export const URL_KEYS = {
  latitude: "lat",
  longitude: "lng",
  zoom: "zoom",

  swLat: "swLat",
  swLng: "swLng",
  neLat: "neLat",
  neLng: "neLng",

  businessTypes: "t",
  statuses: "st",
  listedBy: "lb",
  saleTypes: "sale",
  price: "p",
  revenue: "r",
  profit: "pr",
  sde: "sde",
  sdeMultiple: "sdem",
  revenueMultiple: "rm",
  downPayment: "dp",
  establishmentAge: "age",
  employeeCount: "em",
  ownerWorkedHours: "hrs",
  propertyIncluded: "prop",
  financingAvailable: "fin",
  sbaPrequalified: "sba",
  visaQualified: "visa",
  includeMissingMultiples: "imm",
  priceDirection: "pd",
  priceChangedWithinDays: "pcwd",
  minPriceChangePercent: "mpcp",
  sortBy: "sort",
} as const;

function rangeToStringRange<T extends number | bigint>(range: RangeFilter<T>) {
  if (range.min === null && range.max === null) {
    return null;
  }

  return `${range.min?.toString() ?? ""},${range.max?.toString() ?? ""}`;
}

function stringToRange(type: "bigint", str: string | null): RangeFilter<bigint>;
function stringToRange(type: "number", str: string | null): RangeFilter<number>;
function stringToRange(
  type: "bigint" | "number",
  str: string | null,
): RangeFilter<bigint | number> {
  if (str === null) {
    return { min: null, max: null };
  }

  const Constructor = type === "bigint" ? BigInt : Number;

  const [minStr, maxStr] = str.split(",");
  const min = minStr === "" ? null : Constructor(minStr);
  const max = maxStr === "" ? null : Constructor(maxStr);

  return { min, max };
}

export function getSearchParams(
  filters: Omit<BusinessFilters, "location">,
  initialParams?: URLSearchParams,
) {
  const params = initialParams ?? new URLSearchParams();

  const finalStatuses = filters.statuses.filter((status) =>
    PUBLICLY_FILTERABLE_LISTING_STATUSES.includes(status),
  );

  if (filters.businessTypeIds.length > 0) {
    params.set(URL_KEYS.businessTypes, filters.businessTypeIds.join(","));
  } else {
    params.delete(URL_KEYS.businessTypes);
  }

  if (finalStatuses.length > 0) {
    params.set(URL_KEYS.statuses, finalStatuses.join(","));
  } else {
    params.delete(URL_KEYS.statuses);
  }

  if (filters.listedBy.length > 0) {
    params.set(URL_KEYS.listedBy, filters.listedBy.join(","));
  } else {
    params.delete(URL_KEYS.listedBy);
  }

  if (filters.saleTypes.length > 0) {
    params.set(URL_KEYS.saleTypes, filters.saleTypes.join(","));
  } else {
    params.delete(URL_KEYS.saleTypes);
  }

  const price = rangeToStringRange(filters.priceRange);
  const revenue = rangeToStringRange(filters.revenueRange);
  const profit = rangeToStringRange(filters.profitRange);
  const sde = rangeToStringRange(filters.sdeRange);
  const sdeMultiple = rangeToStringRange(filters.sdeMultipleRange);
  const revenueMultiple = rangeToStringRange(filters.revenueMultipleRange);
  const downPayment = rangeToStringRange(filters.downPaymentRange);
  const establishmentAge = rangeToStringRange(filters.establishmentAgeRange);
  const employeeCount = rangeToStringRange(filters.employeeCountRange);
  const ownerWorkedHours = rangeToStringRange(filters.ownerWorkedHoursRange);

  if (price) {
    params.set(URL_KEYS.price, price);
  } else {
    params.delete(URL_KEYS.price);
  }

  if (revenue) {
    params.set(URL_KEYS.revenue, revenue);
  } else {
    params.delete(URL_KEYS.revenue);
  }

  if (profit) {
    params.set(URL_KEYS.profit, profit);
  } else {
    params.delete(URL_KEYS.profit);
  }

  if (sde) {
    params.set(URL_KEYS.sde, sde);
  } else {
    params.delete(URL_KEYS.sde);
  }

  if (sdeMultiple) {
    params.set(URL_KEYS.sdeMultiple, sdeMultiple);
  } else {
    params.delete(URL_KEYS.sdeMultiple);
  }

  if (revenueMultiple) {
    params.set(URL_KEYS.revenueMultiple, revenueMultiple);
  } else {
    params.delete(URL_KEYS.revenueMultiple);
  }

  if (downPayment) {
    params.set(URL_KEYS.downPayment, downPayment);
  } else {
    params.delete(URL_KEYS.downPayment);
  }

  if (establishmentAge) {
    params.set(URL_KEYS.establishmentAge, establishmentAge);
  } else {
    params.delete(URL_KEYS.establishmentAge);
  }

  if (employeeCount) {
    params.set(URL_KEYS.employeeCount, employeeCount);
  } else {
    params.delete(URL_KEYS.employeeCount);
  }

  if (ownerWorkedHours) {
    params.set(URL_KEYS.ownerWorkedHours, ownerWorkedHours);
  } else {
    params.delete(URL_KEYS.ownerWorkedHours);
  }

  if (filters.propertyIncluded) {
    params.set(URL_KEYS.propertyIncluded, "1");
  } else {
    params.delete(URL_KEYS.propertyIncluded);
  }

  if (filters.financingAvailable) {
    params.set(URL_KEYS.financingAvailable, "1");
  } else {
    params.delete(URL_KEYS.financingAvailable);
  }

  if (filters.sbaPrequalified) {
    params.set(URL_KEYS.sbaPrequalified, "1");
  } else {
    params.delete(URL_KEYS.sbaPrequalified);
  }

  if (filters.visaQualified) {
    params.set(URL_KEYS.visaQualified, "1");
  } else {
    params.delete(URL_KEYS.visaQualified);
  }

  if (!filters.includeMissingMultiples) {
    params.set(URL_KEYS.includeMissingMultiples, "0");
  } else {
    params.delete(URL_KEYS.includeMissingMultiples);
  }

  if (filters.priceDirection) {
    params.set(URL_KEYS.priceDirection, filters.priceDirection);
  } else {
    params.delete(URL_KEYS.priceDirection);
  }

  if (filters.priceChangedWithinDays) {
    params.set(
      URL_KEYS.priceChangedWithinDays,
      String(filters.priceChangedWithinDays),
    );
  } else {
    params.delete(URL_KEYS.priceChangedWithinDays);
  }

  if (filters.minPriceChangePercent) {
    params.set(
      URL_KEYS.minPriceChangePercent,
      String(filters.minPriceChangePercent),
    );
  } else {
    params.delete(URL_KEYS.minPriceChangePercent);
  }

  return params;
}

export function updateSearchParams(
  pathname: string,
  params: URLSearchParams,
  filters: BusinessFilters,
) {
  const prevStr = params.toString();
  const newParams = getSearchParams(filters, params);
  const newStr = newParams.toString();

  if (prevStr === newStr) {
    return;
  }

  window.history.replaceState(
    window.history.state ?? {},
    "",
    `${pathname}?${newParams.toString()}`,
  );
}

export function readFromSearchParams(
  params: URLSearchParams,
): Omit<BusinessFilters, "location"> {
  const businessTypes = params.get(URL_KEYS.businessTypes);
  const statuses = params.get(URL_KEYS.statuses);
  const listedBy = params.get(URL_KEYS.listedBy);
  const saleTypesStr = params.get(URL_KEYS.saleTypes);
  const price = params.get(URL_KEYS.price);
  const revenue = params.get(URL_KEYS.revenue);
  const profit = params.get(URL_KEYS.profit);
  const sde = params.get(URL_KEYS.sde);
  const sdeMultiple = params.get(URL_KEYS.sdeMultiple);
  const revenueMultiple = params.get(URL_KEYS.revenueMultiple);
  const downPayment = params.get(URL_KEYS.downPayment);
  const establishmentAge = params.get(URL_KEYS.establishmentAge);
  const employeeCount = params.get(URL_KEYS.employeeCount);
  const ownerWorkedHours = params.get(URL_KEYS.ownerWorkedHours);

  const propertyIncluded = params.has(URL_KEYS.propertyIncluded);
  const financingAvailable = params.has(URL_KEYS.financingAvailable);
  const sbaPrequalified = params.has(URL_KEYS.sbaPrequalified);
  const visaQualified = params.has(URL_KEYS.visaQualified);
  const includeMissingMultiples =
    params.get(URL_KEYS.includeMissingMultiples) !== "0";

  const priceDirectionStr = params.get(URL_KEYS.priceDirection);
  const priceDirection: PriceDirectionFilter =
    priceDirectionStr === "INCREASED" || priceDirectionStr === "DECREASED"
      ? priceDirectionStr
      : null;

  const priceChangedWithinDaysStr = params.get(URL_KEYS.priceChangedWithinDays);
  const priceChangedWithinDays = priceChangedWithinDaysStr
    ? Number(priceChangedWithinDaysStr)
    : null;

  const minPriceChangePercentStr = params.get(URL_KEYS.minPriceChangePercent);
  const minPriceChangePercent = minPriceChangePercentStr
    ? Number(minPriceChangePercentStr)
    : null;

  return {
    businessTypeIds: businessTypes
      ? businessTypes.split(",").map(Number).filter((n) => !Number.isNaN(n) && n > 0)
      : [],
    statuses: statuses
      ? (statuses
          .split(",")
          .filter((status) =>
            PUBLICLY_FILTERABLE_LISTING_STATUSES.includes(
              status as PubliclyFilterableListingStatus,
            ),
          ) as PubliclyFilterableListingStatus[])
      : [],
    listedBy: listedBy
      ? (listedBy
          .split(",")
          .filter(
            (value): value is ListedByFilter =>
              value === "owner" || value === "broker",
          ) as ListedByFilter[])
      : [],
    saleTypes: saleTypesStr
      ? (saleTypesStr
          .split(",")
          .filter((v): v is SaleTypeType =>
            VALID_SALE_TYPES.includes(v as SaleTypeType),
          ))
      : [],
    priceRange: stringToRange("bigint", price),
    revenueRange: stringToRange("bigint", revenue),
    profitRange: stringToRange("bigint", profit),
    sdeRange: stringToRange("bigint", sde),
    sdeMultipleRange: stringToRange("number", sdeMultiple),
    revenueMultipleRange: stringToRange("number", revenueMultiple),
    downPaymentRange: stringToRange("bigint", downPayment),
    establishmentAgeRange: stringToRange("number", establishmentAge),
    employeeCountRange: stringToRange("number", employeeCount),
    ownerWorkedHoursRange: stringToRange("number", ownerWorkedHours),
    propertyIncluded,
    financingAvailable,
    sbaPrequalified,
    visaQualified,
    includeMissingMultiples,
    priceDirection,
    priceChangedWithinDays,
    minPriceChangePercent,
  };
}

export function readFromSavedSearch(
  savedSearch: DetailedSavedSearch,
): Omit<BusinessFilters, "location"> {
  const { userSearch } = savedSearch;

  return {
    businessTypeIds: userSearch.businessTypes.map(({ id }) => id),
    statuses: userSearch.statuses.filter((status) =>
      PUBLICLY_FILTERABLE_LISTING_STATUSES.includes(
        status as PubliclyFilterableListingStatus,
      ),
    ) as PubliclyFilterableListingStatus[],
    listedBy: [],
    saleTypes: [],
    priceRange: {
      min: userSearch.priceMin || null,
      max: userSearch.priceMax || null,
    },
    revenueRange: {
      min: userSearch.revenueMin || null,
      max: userSearch.revenueMax || null,
    },
    profitRange: {
      min: userSearch.profitMin || null,
      max: userSearch.profitMax || null,
    },
    sdeRange: {
      min: userSearch.sdeMin || null,
      max: userSearch.sdeMax || null,
    },
    sdeMultipleRange: {
      min: userSearch.sdeMultipleMin ?? null,
      max: userSearch.sdeMultipleMax ?? null,
    },
    revenueMultipleRange: {
      min: userSearch.revenueMultipleMin ?? null,
      max: userSearch.revenueMultipleMax ?? null,
    },
    downPaymentRange: {
      min: userSearch.downPaymentMin || null,
      max: userSearch.downPaymentMax || null,
    },
    establishmentAgeRange: {
      min: userSearch.establishedAfter
        ? new Date().getFullYear() - userSearch.establishedAfter.getFullYear()
        : null,
      max: userSearch.establishedBefore
        ? new Date().getFullYear() - userSearch.establishedBefore.getFullYear()
        : null,
    },
    employeeCountRange: {
      min: userSearch.employeesMin || null,
      max: userSearch.employeesMax || null,
    },
    ownerWorkedHoursRange: {
      min: userSearch.ownerWorkedHoursMin || null,
      max: userSearch.ownerWorkedHoursMax || null,
    },
    propertyIncluded: userSearch.propertyIncluded,
    financingAvailable: userSearch.financingAvailable,
    sbaPrequalified: userSearch.sbaPrequalified,
    visaQualified: userSearch.visaQualified,
    includeMissingMultiples: userSearch.includeMissingMultiples ?? true,
    priceDirection:
      userSearch.priceDirection === "INCREASED" ||
      userSearch.priceDirection === "DECREASED"
        ? userSearch.priceDirection
        : null,
    priceChangedWithinDays: userSearch.priceChangedWithinDays ?? null,
    minPriceChangePercent: userSearch.minPriceChangePercent ?? null,
  };
}

/**
 * Converts UI-level BusinessFilters into the compact shape expected by the
 * tRPC listing filter input.  Empty arrays, null ranges, and null booleans
 * are omitted so the superjson-serialized URL stays small.
 */
export function buildListingFilterParams(
  filters: Omit<BusinessFilters, "location">,
) {
  const rangeOrUndefined = <T extends bigint | number>(
    range: RangeFilter<T>,
  ): { min?: T; max?: T } | undefined => {
    const min = range.min ?? undefined;
    const max = range.max ?? undefined;
    if (min === undefined && max === undefined) return undefined;
    return { min, max };
  };

  return {
    businessTypeIds:
      filters.businessTypeIds.length > 0
        ? filters.businessTypeIds
        : undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    listedBy: filters.listedBy.length > 0 ? filters.listedBy : undefined,
    saleTypes: filters.saleTypes.length > 0 ? filters.saleTypes : undefined,
    price: rangeOrUndefined(filters.priceRange),
    revenue: rangeOrUndefined(filters.revenueRange),
    profit: rangeOrUndefined(filters.profitRange),
    sde: rangeOrUndefined(filters.sdeRange),
    sdeMultiple: rangeOrUndefined(filters.sdeMultipleRange),
    revenueMultiple: rangeOrUndefined(filters.revenueMultipleRange),
    downPayment: rangeOrUndefined(filters.downPaymentRange),
    establishmentAge: rangeOrUndefined(filters.establishmentAgeRange),
    employeeCount: rangeOrUndefined(filters.employeeCountRange),
    ownerWorkedHours: rangeOrUndefined(filters.ownerWorkedHoursRange),
    propertyIncluded: filters.propertyIncluded ?? undefined,
    buyerFinancingAvailable: filters.financingAvailable ?? undefined,
    sbaPrequalified: filters.sbaPrequalified ?? undefined,
    visaQualified: filters.visaQualified ?? undefined,
    includeMissingMultiples: filters.includeMissingMultiples,
    priceDirection: filters.priceDirection ?? undefined,
    priceChangedWithinDays: filters.priceChangedWithinDays ?? undefined,
    minPriceChangePercent: filters.minPriceChangePercent ?? undefined,
  };
}

export function savedSearchToSearchParams(
  savedSearch: DetailedSavedSearch,
  initialParams?: URLSearchParams,
) {
  const filters = readFromSavedSearch(savedSearch);
  return getSearchParams(filters, initialParams);
}
